import { Node } from 'reactflow';
import { ArchitectureNode } from '../types';

export type AlignmentType = 
  | 'align-left' 
  | 'align-center' 
  | 'align-right' 
  | 'align-top' 
  | 'align-middle' 
  | 'align-bottom'
  | 'distribute-horizontal'
  | 'distribute-vertical'
  | 'match-width'
  | 'match-height';

export const snapToGrid = (value: number, gridSize: number): number => {
  return Math.round(value / gridSize) * gridSize;
};

export const alignNodes = (
  nodes: Node[],
  alignment: AlignmentType,
  selectedNodeIds: string[]
): Node[] => {
  if (selectedNodeIds.length < 2) return nodes;

  const selectedNodes = nodes.filter(n => selectedNodeIds.includes(n.id));
  if (selectedNodes.length < 2) return nodes;

  return nodes.map(node => {
    if (!selectedNodeIds.includes(node.id)) return node;
    
    const newNode = { ...node, position: { ...node.position } };
    if (newNode.style) {
      newNode.style = { ...newNode.style };
    }

    switch (alignment) {
      case 'align-left': {
        const minX = Math.min(...selectedNodes.map(n => n.position.x));
        newNode.position.x = minX;
        break;
      }
      case 'align-right': {
        const maxX = Math.max(...selectedNodes.map(n => {
          const width = typeof n.width === 'number' ? n.width : (n.style?.width as number) || 75;
          return n.position.x + width;
        }));
        const width = typeof node.width === 'number' ? node.width : (node.style?.width as number) || 75;
        newNode.position.x = maxX - width;
        break;
      }
      case 'align-center': {
        const centers = selectedNodes.map(n => {
          const width = typeof n.width === 'number' ? n.width : (n.style?.width as number) || 75;
          return n.position.x + width / 2;
        });
        const avgCenter = centers.reduce((a, b) => a + b, 0) / centers.length;
        const width = typeof node.width === 'number' ? node.width : (node.style?.width as number) || 75;
        newNode.position.x = avgCenter - width / 2;
        break;
      }
      case 'align-top': {
        const minY = Math.min(...selectedNodes.map(n => n.position.y));
        newNode.position.y = minY;
        break;
      }
      case 'align-bottom': {
        const maxY = Math.max(...selectedNodes.map(n => {
          const height = typeof n.height === 'number' ? n.height : (n.style?.height as number) || 50;
          return n.position.y + height;
        }));
        const height = typeof node.height === 'number' ? node.height : (node.style?.height as number) || 50;
        newNode.position.y = maxY - height;
        break;
      }
      case 'align-middle': {
        const centers = selectedNodes.map(n => {
          const height = typeof n.height === 'number' ? n.height : (n.style?.height as number) || 50;
          return n.position.y + height / 2;
        });
        const avgCenter = centers.reduce((a, b) => a + b, 0) / centers.length;
        const height = typeof node.height === 'number' ? node.height : (node.style?.height as number) || 50;
        newNode.position.y = avgCenter - height / 2;
        break;
      }
      case 'distribute-horizontal': {
        const sorted = [...selectedNodes].sort((a, b) => a.position.x - b.position.x);
        const nodeIndex = sorted.findIndex(n => n.id === node.id);
        if (nodeIndex > 0 && nodeIndex < sorted.length - 1) {
          const first = sorted[0];
          const last = sorted[sorted.length - 1];
          const firstRight = first.position.x + (typeof first.width === 'number' ? first.width : (first.style?.width as number) || 75);
          const lastLeft = last.position.x;
          const totalGap = lastLeft - firstRight;
          const gap = totalGap / (sorted.length - 1);
          
          let currentX = firstRight;
          for (let i = 1; i < nodeIndex; i++) {
            const width = typeof sorted[i].width === 'number' ? sorted[i].width : (sorted[i].style?.width as number) || 75;
            currentX += width + gap;
          }
          newNode.position.x = currentX;
        }
        break;
      }
      case 'distribute-vertical': {
        const sorted = [...selectedNodes].sort((a, b) => a.position.y - b.position.y);
        const nodeIndex = sorted.findIndex(n => n.id === node.id);
        if (nodeIndex > 0 && nodeIndex < sorted.length - 1) {
          const first = sorted[0];
          const last = sorted[sorted.length - 1];
          const firstBottom = first.position.y + (typeof first.height === 'number' ? first.height : (first.style?.height as number) || 50);
          const lastTop = last.position.y;
          const totalGap = lastTop - firstBottom;
          const gap = totalGap / (sorted.length - 1);
          
          let currentY = firstBottom;
          for (let i = 1; i < nodeIndex; i++) {
            const height = typeof sorted[i].height === 'number' ? sorted[i].height : (sorted[i].style?.height as number) || 50;
            currentY += height + gap;
          }
          newNode.position.y = currentY;
        }
        break;
      }
      case 'match-width': {
        const widths = selectedNodes.map(n => typeof n.width === 'number' ? n.width : (n.style?.width as number) || 75);
        const maxWidth = Math.max(...widths);
        newNode.style = { ...newNode.style, width: maxWidth };
        break;
      }
      case 'match-height': {
        const heights = selectedNodes.map(n => typeof n.height === 'number' ? n.height : (n.style?.height as number) || 50);
        const maxHeight = Math.max(...heights);
        newNode.style = { ...newNode.style, height: maxHeight };
        break;
      }
    }
    
    return newNode;
  });
};
